const DEFAULT_WIDGETS_SCRIPT = 'https://donorbox.org/widgets.js';
const WIDGET_TAG = 'dbox-widget';
const SHADOW_STYLE_ID = 'lj-donorbox-checkout-layout';

const injectDonorboxShadowStyles = (shadowRoot) => {
  if (!shadowRoot || shadowRoot.querySelector(`#${SHADOW_STYLE_ID}`)) return;

  const style = document.createElement('style');
  style.id = SHADOW_STYLE_ID;
  style.textContent = `
    #express_checkout_section,
    #stripe_express_checkout {
      min-height: 0 !important;
    }
    #express_checkout_section p {
      margin-bottom: 0.35rem;
    }
    #standard_checkout_section {
      margin-top: 0;
    }
  `;
  shadowRoot.appendChild(style);
};

const reorderDonorboxCheckoutSections = (widget) => {
  const shadowRoot = widget?.shadowRoot;
  if (!shadowRoot) return;

  injectDonorboxShadowStyles(shadowRoot);

  const panels = shadowRoot.querySelectorAll(
    '#standard_donation_panel, #payment_methods_tabs, #page_payment'
  );

  panels.forEach((panel) => {
    const express = panel.querySelector('#express_checkout_section');
    const standard = panel.querySelector('#standard_checkout_section');
    if (!express || !standard) return;

    const expressFollowsStandard =
      Boolean(express.compareDocumentPosition(standard) & Node.DOCUMENT_POSITION_PRECEDING);
    if (expressFollowsStandard) return;

    panel.appendChild(express);
  });
};

const clampDonorboxExpressCheckoutHeight = (container) => {
  if (!container) return;

  container.querySelectorAll('[slot="express_checkout"]').forEach((slot) => {
    slot.style.minHeight = '0';

    slot.querySelectorAll('.__PrivateStripeElement, iframe').forEach((node) => {
      if (node.tagName === 'IFRAME' && node.offsetHeight > 72) {
        node.style.height = '55px';
      }
      node.style.minHeight = '0';
    });
  });
};

export const optimizeDonorboxCheckoutLayout = (container) => {
  if (typeof window === 'undefined' || !container) return () => {};

  const widget = container.querySelector(WIDGET_TAG);
  if (!widget) return () => {};

  const run = () => {
    reorderDonorboxCheckoutSections(widget);
    clampDonorboxExpressCheckoutHeight(container);
    scheduleDonorboxResize(container);
  };

  run();

  const observers = [];

  if (widget.shadowRoot) {
    observers.push(
      new MutationObserver(run)
    );
    observers[observers.length - 1].observe(widget.shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });
  }

  observers.push(new MutationObserver(run));
  observers[observers.length - 1].observe(widget, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style'],
  });

  const timers = [100, 300, 600, 1200, 2500, 5000].map((delay) => setTimeout(run, delay));

  return () => {
    observers.forEach((observer) => observer.disconnect());
    timers.forEach(clearTimeout);
  };
};

export const parseDonorboxEmbedCode = (embedCode) => {
  if (!embedCode?.trim() || typeof DOMParser === 'undefined') return null;

  const doc = new DOMParser().parseFromString(embedCode.trim(), 'text/html');
  const script = doc.querySelector('script[src*="donorbox.org"]');
  const widget = doc.querySelector(WIDGET_TAG);

  if (!widget) return null;

  return {
    scriptSrc: script?.getAttribute('src') || DEFAULT_WIDGETS_SCRIPT,
    scriptType: script?.getAttribute('type') || 'module',
    widgetHtml: widget.outerHTML,
  };
};

let widgetsScriptPromise = null;

const ensureWidgetsScript = (scriptSrc, scriptType) => {
  if (typeof document === 'undefined') return Promise.resolve();

  if (customElements.get(WIDGET_TAG) && document.querySelector(`script[src="${scriptSrc}"]`)) {
    return Promise.resolve();
  }

  if (!document.querySelector(`script[src="${scriptSrc}"]`)) {
    if (!widgetsScriptPromise) {
      widgetsScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = scriptType;
        script.src = scriptSrc;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      }).catch((error) => {
        widgetsScriptPromise = null;
        throw error;
      });
    }
    return widgetsScriptPromise;
  }

  return Promise.resolve();
};

const waitForDboxWidget = () => {
  if (typeof customElements === 'undefined') return Promise.resolve();
  if (customElements.get(WIDGET_TAG)) return Promise.resolve();
  return customElements.whenDefined(WIDGET_TAG);
};

const nextFrame = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

const mountDboxWidget = (container, widgetHtml) => {
  if (container.querySelector(WIDGET_TAG)) return container.querySelector(WIDGET_TAG);
  container.innerHTML = widgetHtml;
  return container.querySelector(WIDGET_TAG);
};

export const loadDonorboxOmniGiveEmbed = (container, embedCode) => {
  if (!container || !embedCode) return () => {};

  const parsed = parseDonorboxEmbedCode(embedCode);
  if (!parsed?.widgetHtml) return () => {};

  let cancelled = false;
  let layoutCleanup = () => {};
  container.innerHTML = '';

  ensureWidgetsScript(parsed.scriptSrc, parsed.scriptType)
    .then(() => waitForDboxWidget())
    .then(() => nextFrame())
    .then(() => {
      if (cancelled || !container.isConnected) return;
      mountDboxWidget(container, parsed.widgetHtml);
      scheduleDonorboxResize(container);
      layoutCleanup = optimizeDonorboxCheckoutLayout(container);
    })
    .catch(() => {});

  return () => {
    cancelled = true;
    layoutCleanup();
    container.innerHTML = '';
  };
};

export const resizeDonorboxWidget = (container) => {
  if (typeof window === 'undefined' || !window.donorbox?.resizeDonationWidget) return;

  const iframe = container?.querySelector?.('iframe');
  if (iframe) {
    window.donorbox.resizeDonationWidget(iframe);
    return;
  }

  const widget = container?.querySelector?.(WIDGET_TAG);
  if (widget) {
    window.donorbox.resizeDonationWidget(widget);
  }
};

export const scheduleDonorboxResize = (container) => {
  if (!container) return;

  const attempt = () => resizeDonorboxWidget(container);
  attempt();
  [100, 300, 600, 1200].forEach((delay) => {
    setTimeout(attempt, delay);
  });
};

export const isOmniGiveEmbedCode = (embedCode) =>
  Boolean(embedCode && String(embedCode).includes(WIDGET_TAG));
